import React, {useEffect, useRef} from 'react'

import L from '../components/map/leaflet.js'
import {createTileLayer, fitBoundsSafe} from '../components/map/setup.js'
import {Translator} from './i18n.js'
import {TrackTraceMap} from '../../types/trackTrace.js'

interface Props {
    tr: Translator
    map: TrackTraceMap
    courierIcon: 'bike' | 'cargo-bike' | 'car'
}

const courierIcons = {'bike': 'bicycle', 'cargo-bike': 'bicycle-cargo', 'car': 'car'}

function markerIcon(className: string, icon: string, label: string) {
    return L.divIcon({
        className: '',
        html: `<div class="tt-marker ${className}" role="img" aria-label="${label}"><i class="mdi mdi-${icon}" aria-hidden="true"></i></div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18]
    })
}

/** Volg je bezorger: the recipient's address and, on the day of delivery, the courier. Never other stops. */
export default function MapCard({tr, map, courierIcon}: Props) {
    const {t} = tr
    const element = useRef<HTMLDivElement>(null)
    const leaflet = useRef<{map: L.Map, home: L.Marker, courier: L.Marker | null} | null>(null)

    useEffect(() => {
        const instance = L.map(element.current, {attributionControl: false, zoomControl: false, scrollWheelZoom: false})
        createTileLayer().addTo(instance)

        leaflet.current = {map: instance, home: L.marker(map.recipient, {icon: markerIcon('tt-marker-home', 'home', t('mapYourAddress')), keyboard: false}).addTo(instance), courier: null}

        return () => {
            instance.remove()
            leaflet.current = null
        }
    }, [])

    // Refreshes move the markers, the view only follows when the courier appears or moves
    useEffect(() => {
        const current = leaflet.current

        if (!current) return

        current.home.setLatLng(map.recipient)

        if (map.courier) {
            if (current.courier) {
                current.courier.setLatLng(map.courier)
            } else {
                current.courier = L.marker(map.courier, {icon: markerIcon('tt-marker-courier', courierIcons[courierIcon], t('mapCourier')), keyboard: false}).addTo(current.map)
            }
        } else if (current.courier) {
            current.courier.remove()
            current.courier = null
        }

        fitBoundsSafe(current.map, [map.recipient, map.courier], {padding: [48, 48], pointZoom: 15})
    }, [map.recipient.lat, map.recipient.lng, map.courier?.lat, map.courier?.lng])

    const texts = [
        map.stopsBefore !== null ? t('mapStops', {count: map.stopsBefore}) : '',
        map.courier && map.eta ? t('mapEta', {time: map.eta}) : ''
    ].filter(Boolean)

    return (
        <section className='tt-card' aria-labelledby='tt-map-title'>
            <div className='tt-row-text' style={{gap: 3}}>
                <h2 id='tt-map-title' className='tt-title-medium'>{t('mapTitle')}</h2>
                {texts.length > 0 && <p>{texts.join(' ')}</p>}
            </div>

            <div className='tt-map'>
                <div ref={element} style={{position: 'absolute', inset: 0}} aria-hidden='true' />
                {map.stopsBefore !== null &&
                    <span className='tt-map-stops tt-body-small'><i className='mdi mdi-map-marker-path' aria-hidden='true' />{t('mapStopsLabel', {count: map.stopsBefore})}</span>
                }
            </div>

            <p className='tt-body-small'>
                {[t('mapOnlyToday'), map.courier ? t('mapUpdated', {time: tr.time(new Date(map.courier.updatedAt))}) : ''].filter(Boolean).join(' ')}
            </p>
        </section>
    )
}
